import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { KeyRound, Check, AlertCircle } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { PASSWORD_MIN_LENGTH } from '../../../config/constants';

/**
 * Eén ondergrens, uit de gedeelde constante.
 *
 * Dit stond hier drie keer los ingetikt (`8` bij het eerste veld, `role ===
 * 'learner' ? 4 : 6` bij het tweede én bij de knop). De toelichting hieronder
 * beweerde al dat het overal 8 was, maar twee van de drie plekken waren nooit
 * meegegaan — een kind kon met vier tekens op "Account aanmaken" drukken en
 * liep dan alsnog tegen de servereis aan. Precies het doodlopende pad dat die
 * toelichting zegt te hebben opgelost.
 */
const MIN_PASSWORD_LENGTH = PASSWORD_MIN_LENGTH.default;
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterPasswordStepProps {
  role: Role;
  password: string;
  passwordConfirm: string;
  validationError: string;
  isSubmitting: boolean;
  onPasswordChange: (value: string) => void;
  onPasswordConfirmChange: (value: string) => void;
  slideVariants: Record<string, unknown>;
}
export function RegisterPasswordStep({
  role,
  password,
  passwordConfirm,
  validationError,
  isSubmitting,
  onPasswordChange,
  onPasswordConfirmChange,
  slideVariants
}: RegisterPasswordStepProps) {
  return <motion.div key="step-password" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3
  }} className="space-y-5" data-id="element-419" data-mp="ft3r6rv1ncat">
      
      <h2 className="text-xl font-black text-slate-900 text-center" data-id="element-420" data-mp="pqrnhm8asrv3">
        Kies een wachtwoord
      </h2>

      <div data-id="element-421" data-mp="ts3i666143a5">
        <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-422" data-mp="tsll1ln75fr1">
          Wachtwoord
        </label>
        {/*
           8 tekens voor iedereen. Dit stond op 4 voor leerlingen en 6 voor
           volwassenen, terwijl de server 8 eist — zowel de Edge Function
           `create-learner` als Supabase Auth weigeren korter. Een kind vulde
           dus vier tekens in, kreeg groen licht van het formulier, en liep
           daarna tegen een serverfout aan die het onmogelijk kan plaatsen. Een
           formulier dat losser is dan de server is geen vriendelijkheid maar
           een doodlopend pad.
          */}
        <div className="relative" data-id="element-423" data-mp="a4vehko0fopu">
          <Input type="password" required autoFocus minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" value={password} onChange={e => onPasswordChange(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-425" data-mp="3ekeq6q6acftm" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium tracking-wider py-4" placeholder="••••••••" data-id="element-424" data-mp="a2iuz32lt6ao" />
          
        </div>
        <p className="text-xs text-slate-500 mt-2 ml-1" data-id="element-426" data-mp="2ztnuv85hiylr">
          {role === 'learner' ? 'Minimaal 8 tekens — kies bijvoorbeeld twee woorden achter elkaar' : 'Minimaal 8 tekens'}
        </p>
        {/* Password strength hint */}
        {password.length > 0 && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="mt-2 flex gap-1.5" data-id="element-427" data-mp="1aaz6s6doqeq9">
          
            {[1, 2, 3, 4].map(level => {
          const minLen = MIN_PASSWORD_LENGTH;
          const strength = password.length >= minLen + 4 ? 4 : password.length >= minLen + 2 ? 3 : password.length >= minLen ? 2 : 1;
          const colors = ['bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-emerald-400'];
          return <div key={level} className={`h-1.5 flex-1 rounded-full transition-colors ${level <= strength ? colors[strength - 1] : 'bg-slate-200'}`} data-id="element-428" data-mp="1y2mnxwl5abfc" />;
        })}
          </motion.div>}
      </div>

      <div data-id="element-429" data-mp="1p0wygwatpmk7">
        <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-430" data-mp="1p0n6x4a4r7ce">
          Herhaal wachtwoord
        </label>
        <div className="relative" data-id="element-431" data-mp="8mez6fu0r2kt">
          <Input type="password" required minLength={MIN_PASSWORD_LENGTH} autoComplete="new-password" value={passwordConfirm} onChange={e => onPasswordConfirmChange(e.target.value)} icon={<KeyRound className="w-5 h-5" data-id="element-433" data-mp="sx7nrz9sp6ft" />} className={`bg-slate-50 border-2 rounded-2xl focus:bg-white text-lg font-medium tracking-wider py-4 ${passwordConfirm && password !== passwordConfirm ? 'border-red-300 focus:border-red-400' : passwordConfirm && password === passwordConfirm ? 'border-emerald-400 focus:border-emerald-500' : 'border-slate-200 focus:border-orange-400'}`} placeholder="Herhaal je wachtwoord" data-id="element-432" data-mp="8pj97tu60sje" />
          
          {passwordConfirm && password === passwordConfirm && <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500 z-10" data-id="element-434" data-mp="17ui900c8pqoq" />}
        </div>
        {passwordConfirm && password !== passwordConfirm && <p className="text-xs text-red-500 font-medium mt-1.5 flex items-center gap-1" data-id="element-435" data-mp="xviwu7s9ncwz">
            <AlertCircle className="w-3 h-3" data-id="element-436" data-mp="3nk1rwkzn53tj" /> Wachtwoorden komen niet overeen
          </p>}
      </div>

      <AnimatePresence data-id="element-437" data-mp="3ili56id83o04">
        {validationError && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-438" data-mp="3ijnhwxehbjsv">
          
            <div className="p-3 bg-red-50 text-red-700 text-sm font-medium rounded-2xl border border-red-200 flex items-start gap-2" data-id="element-439" data-mp="3ifhhzwef5o7x">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-440" data-mp="3n798k6xkrty2" />
              <span data-id="element-441" data-mp="3r81i71ad00uv">{validationError}</span>
            </div>
          </motion.div>}
      </AnimatePresence>

      <Button type="submit" variant="primary" size="xl" className="w-full" disabled={isSubmitting || password.length < MIN_PASSWORD_LENGTH || password !== passwordConfirm} data-id="element-442" data-mp="18adel293hd">
        
        {isSubmitting ? 'Even geduld...' : 'Account aanmaken 🎉'}
      </Button>
    </motion.div>;
}